import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, Database, ChevronLeft, ChevronRight, BrainCircuit, Users, Building2, MessageSquareText, LogOut, Cpu, Download } from 'lucide-react';
import { useAuth } from 'client-types/*';
import { cn } from '@/lib/utils';
import { useAppConfig } from '@/hooks/useAppConfig';

import NewChatButton from '@/components/header/NewChat';
import { ConversationHistory } from '@/components/LeftSidebar/ConversationHistory';

const baseNavItems = [
  { label: '智能工作區', icon: MessageSquare, path: '/chat' },
];

const orgUserNavItems = [
  { label: '知識庫管理', icon: Database, path: '/knowledge-base' },
  { label: '資料匯出', icon: Download, path: '/export' },
];

// org_viewer 唯讀，但可匯出自己的對話紀錄（2026-09-15 客戶需求）
const orgViewerNavItems = [
  { label: '資料匯出', icon: Download, path: '/export' },
];

const superAdminNavItems = [
  { label: '知識庫管理', icon: Database, path: '/knowledge-base' },
  { label: '業者管理', icon: Building2, path: '/admin/organizations' },
  { label: '使用者管理', icon: Users, path: '/admin' },
  { label: '系統提示詞', icon: MessageSquareText, path: '/admin/system-prompts' },
  { label: '資料匯出', icon: Download, path: '/export' },
];

// 單機版：功能全保留，但沒有帳號與業者的概念，故不顯示這兩頁。
// 模型管理僅單機版顯示（線上版依 D4 先隱藏——見 docs/MODEL_MANAGEMENT_INTEGRATION_PLAN.md）。
const singleUserNavItems = [
  { label: '知識庫管理', icon: Database, path: '/knowledge-base' },
  { label: '系統提示詞', icon: MessageSquareText, path: '/admin/system-prompts' },
  { label: '模型管理', icon: Cpu, path: '/admin/model-management' },
  { label: '資料匯出', icon: Download, path: '/export' },   // v1.2.0 起單機版開放
];

interface AppSidebarContentProps {
  /** 桌面收合模式：只顯示圖示、隱藏對話紀錄 */
  collapsed?: boolean;
  /** 點選導覽項目／對話後呼叫（行動版 Sheet 用來關閉抽屜） */
  onNavigate?: () => void;
}

/**
 * 側欄內容（導覽 + 對話紀錄 + 使用者/登出）。
 * 桌面版由 AppSidebar 直接使用；行動版由 AppTopBar 的漢堡選單 Sheet 使用。
 * 父層需為 flex-col 容器。
 */
export function AppSidebarContent({ collapsed = false, onNavigate }: AppSidebarContentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const appConfig = useAppConfig();
  // 單機版沒有登入，useAuth() 的 user 為 null（role 因而是 undefined），
  // 不能沿用線上版的角色判斷，否則側欄只會剩下「智能工作區」。
  const singleUserMode = !!appConfig?.single_user_mode;
  const role = (user?.metadata as Record<string, unknown>)?.role as string | undefined;
  const extraItems = singleUserMode
    ? singleUserNavItems
    : role === 'super_admin'
      ? superAdminNavItems
      : role === 'org_user'
        ? orgUserNavItems
        : role === 'org_viewer'
          ? orgViewerNavItems
          : [];
  const navItems = [...baseNavItems, ...extraItems];

  const handleLogout = async () => {
    // logout() 清除 cookie 與 Recoil user state，但 useUserManagement 的
    // SWR /user 快取不會被清——SPA 導航到 /login 後，快取裡的舊 user 會被
    // effect 塞回 state，Login 頁的 useEffect 又把頁面彈回 /（造成「登出要
    // 按好幾次」）。改用整頁導向讓 SWR 快取隨頁面丟棄。
    await logout();
    window.location.replace('/login');
  };

  const go = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <>
      {/* Nav items */}
      <nav className="flex flex-col gap-1 p-2 shrink-0">
        {navItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path === '/chat' && (location.pathname === '/' || location.pathname === '/chat'));

          return (
            <button
              key={item.path}
              onClick={() => go(item.path)}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full text-left',
                isActive
                  ? 'bg-accent text-primary'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              )}
            >
              <item.icon className="size-5 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* 對話紀錄（收合模式下隱藏，僅保留空白撐高） */}
      {collapsed ? (
        <div className="flex-1" />
      ) : (
        <div className="flex flex-col flex-1 min-h-0 border-t border-border">
          <div className="flex items-center justify-between pl-4 pr-1 py-1 shrink-0">
            <span className="text-xs font-medium text-muted-foreground">對話紀錄</span>
            <NewChatButton navigate={go} />
          </div>
          <ConversationHistory onSelect={onNavigate} />
        </div>
      )}

      {/* User info + Logout（單機版沒有帳號概念，整區不顯示） */}
      {!singleUserMode && (
        <div className="border-t border-border p-2 space-y-1 shrink-0">
          {!collapsed && user && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground truncate">
              {user.identifier}
            </div>
          )}
          <button
            onClick={handleLogout}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-semibold w-full transition-colors',
              'bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white'
            )}
          >
            <LogOut className="size-5 flex-shrink-0" />
            {!collapsed && <span>登出</span>}
          </button>
        </div>
      )}
    </>
  );
}

export default function AppSidebar() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className={cn(
        // 行動版（< md）隱藏固定側欄，改由 AppTopBar 的漢堡選單 Sheet 呈現
        'hidden md:flex flex-col bg-background border-r border-border transition-all duration-300 flex-shrink-0',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="flex items-center h-[60px] px-4 border-b border-border overflow-hidden shrink-0">
        <BrainCircuit className="text-primary size-6 flex-shrink-0" />
        {!collapsed && (
          <span className="ml-2 font-semibold text-foreground whitespace-nowrap text-sm">
            EdgeMindAI
          </span>
        )}
      </div>

      <AppSidebarContent collapsed={collapsed} />

      {/* Collapse button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 px-4 py-3 text-muted-foreground hover:text-foreground border-t border-border text-sm transition-colors shrink-0"
      >
        {collapsed ? (
          <ChevronRight className="size-4" />
        ) : (
          <>
            <ChevronLeft className="size-4" />
            <span>收合</span>
          </>
        )}
      </button>
    </div>
  );
}
